<template>
    <form class="filhantering-redigera-fil-form">
        <h2 v-if="deleteFile && isGenerellFil">Radera fil</h2>
        <h2 v-else-if="isGenerellFil">Koppla fil till patient</h2>
        <h2 v-else>Redigera fil</h2>
        <div class="form-group k3-form fullscreen">
            <div class="k3-sub-form-flex-container">
                <div>
                    <div class="circle">
                        <div class="number">1</div>
                    </div>
                    <div class="vr"></div>
                </div>
                <div style="width: 100%">
                    <div class="left-pad" @click="togglePaneOne()">
                        <h3>
                            Fildata
                            <img
                                :class="['k3-dropdown-icon', paneOneOpen ? 'k3-img-rotate' : '']"
                                src="@/assets/dropdown-arrow-blue.svg"
                            />
                        </h3>
                    </div>

                    <collapse-transition>
                        <div class="k3-sub-form" v-if="paneOneOpen">
                            <FilhanteringForhandsvisning
                                :show-preview="showPreview"
                                class="margin-align"
                                v-bind:url="urlTillFilAttForhandsvisa"
                                v-bind:metaData="forhandsvisningMetaData"
                                v-bind:uf="ursprungligtFilnamn"
                                v-bind:tabId="tabId"
                            ></FilhanteringForhandsvisning>

                            <template v-if="deleteFile">
                                <input-wrapper
                                    class="input-grid-1-col"
                                    :component="DisplayTextWidget"
                                    label="Inläsningsdatum"
                                    name="inmatningsDatum"
                                    v-model="inmatningsDatum"
                                />

                                <input-wrapper
                                    class="input-grid-1-col"
                                    :component="DisplayTextWidget"
                                    label="Inläst av"
                                    name="createdByFullName"
                                    v-model="createdByFullName"
                                />
                            </template>
                            <template v-else>
                                <FilhanteringPersonInlastFil class="whole" @changePatient="changePatient" />

                                <input-wrapper
                                    class="input-grid-1-col"
                                    :component="DisplayTextWidget"
                                    label="Inläsningsdatum"
                                    name="inmatningsDatum"
                                    v-model="inmatningsDatum"
                                />

                                <input-wrapper
                                    class="input-grid-1-col"
                                    :component="widgets.DateWidget"
                                    label="Dokumentdatum"
                                    name="dokumentDatum"
                                    v-model="dokumentDatum"
                                    :required="false"
                                />

                                <input-wrapper
                                    class="input-grid-1-col"
                                    :component="DisplayTextWidget"
                                    label="Inläst av"
                                    name="createdByFullName"
                                    v-model="createdByFullName"
                                />

                                <input-wrapper
                                    class="input-grid-1-col"
                                    :component="FilhanteringSuggestWidget"
                                    label="Dokumenttyp"
                                    name="dokumentTyp"
                                    v-model="dokumentTyp"
                                    apisearch="/filhantering/dokumenttyp/"
                                    :required="true"
                                    :map="
                                        (el) => {
                                            return {
                                                id: el.id,
                                                code: el.id,
                                                displayName: el.namn,
                                            };
                                        }
                                    "
                                />

                                <input-wrapper
                                    class="input-grid-2-col"
                                    :component="widgets.ScalableTextArea"
                                    label="Kommentar"
                                    name="kommentar"
                                    v-model="kommentar"
                                    :maxlength="255"
                                />
                            </template>
                        </div>
                    </collapse-transition>
                </div>
            </div>
        </div>
        <div class="button-row">
            <ButtonGroup>
                <Button v-if="deleteFile" size="primary" type="button" variant="alert" @click="removeFile">
                    <span> Radera fil </span>
                </Button>
                <Button
                    v-else-if="isGenerellFil"
                    size="primary"
                    type="button"
                    @click="connectToPatient"
                    :disabled="!formIsValid || !patient"
                >
                    <span> Koppla fil till patient </span>
                </Button>
                <Button v-else size="primary" type="button" @click="save" :disabled="!formIsValid">
                    <span> Spara och stäng </span>
                </Button>
                <Button size="secondary" variant="contour" type="button" @click="cancel">Avbryt</Button>
            </ButtonGroup>
            <ButtonGroup v-if="!isGenerellFil">
                <Button size="secondary" variant="alert" type="button" role="button" @click="clearFields">
                    Rensa alla fält
                </Button>
            </ButtonGroup>
        </div>
    </form>
</template>

<script>
    import { CollapseTransition } from "@ivanv/vue-collapse-transition";
    import ButtonGroup from "@/components/ButtonGroup.vue";
    import Button from "@/components/Button.vue";
    import widgets from "@/components/widgets/inputs";
    import InputWrapper from "@/components/widgets/inputs/InputWrapper.vue";
    import { getConfig, getErrorMessage, klinikenApi } from "@/api";
    import FilhanteringPersonInlastFil from "./FilhanteringPersonInlastFil.vue";
    import FilhanteringForhandsvisning from "./Forhandsvisning/FilhanteringForhandsvisning.vue";
    import FilhanteringSuggestWidget from "./FilhanteringSuggestWidget.vue";
    import DisplayTextWidget from "@/components/widgets/inputs/DisplayTextWidget.vue";
    import { bytesToSize } from "./Utils.js";
    import dialogEvents from "@/dialogEvents";
    import { mapGetters } from "vuex";
    import { inject } from "vue";
    import { flashMessage, openDialog } from "@/utils";
    import moment from "moment";

    const metaDataGetterAndSetter = (key) => {
        return {
            get() {
                return this.$store.state.filhantering.redigering.metaData[key];
            },

            set(value) {
                this.$store.commit("filhantering/redigeringAngeMetaData", { key, value });
                this.$store.commit("filhantering/redigeringSetFormDirty");
            },
        };
    };

    export default {
        name: "FilhanteringRedigeraFilForm",

        components: {
            InputWrapper,
            CollapseTransition,
            FilhanteringPersonInlastFil,
            FilhanteringForhandsvisning,
            ButtonGroup,
            Button,
        },
        setup() {
            const isGenerellFil = inject("isGenerellFil");
            const deleteFile = inject("deleteFile");

            return { isGenerellFil, deleteFile };
        },
        props: {
            showPreview: {
                type: Boolean,
                required: false,
                default: false,
            },
            tabId: Number,
        },
        data() {
            return {
                paneOneOpen: true,
                paneTwoOpen: false,
                personIdIsValid: false,
                personIdIsUnique: false,
                formErrors: [],
                widgets,
                FilhanteringPersonInlastFil,
                DisplayTextWidget,
                FilhanteringSuggestWidget,

                forhandsvisningMetaData: {
                    ursprungligtFilnamn: this.$store.state.filhantering.redigering.metaData.ursprungligtFilnamn,
                    filstorlek: bytesToSize(this.$store.state.filhantering.redigering.metaData.filstorlek),
                    filformat: this.$store.state.filhantering.redigering.metaData.filformat,
                },

                patient: null,
            };
        },

        computed: {
            kommentar: metaDataGetterAndSetter("kommentar"),
            inmatningsDatum: metaDataGetterAndSetter("inmatningsDatum"),
            dokumentDatum: metaDataGetterAndSetter("dokumentDatum"),
            legalAuthenticator: metaDataGetterAndSetter("legalAuthenticator"),
            createdBy: metaDataGetterAndSetter("createdBy"),
            createdByFullName: metaDataGetterAndSetter("createdByFullName"),
            dokumentTyp: metaDataGetterAndSetter("dokumentTyp"),
            patientUUID: metaDataGetterAndSetter("patientUUID"),
            fileId: metaDataGetterAndSetter("fileId"),
            ursprungligtFilnamn: metaDataGetterAndSetter("ursprungligtFilnamn"),
            ...mapGetters("filhantering", ["redigeringFormIsClean", "redigering"]),
            formIsValid() {
                let result = true;

                // Check date, TODO check that it is actually valid
                // if (!this.dokumentDatum) {
                //    result = false;
                // }

                // Check that dokumentTyp is set. TODO check that it is valid
                if (!this.dokumentTyp) {
                    result = false;
                }

                return result;
            },

            urlTillFilAttForhandsvisa() {
                if (this.isGenerellFil) {
                    return `filhantering/generellafiler/${this.fileId}/file/`;
                } else {
                    return `/filhantering/${this.patientUUID}/sendfile/${this.fileId}/`;
                }
            },
        },

        methods: {
            togglePaneOne() {
                this.paneOneOpen = !this.paneOneOpen;
            },

            clearFields() {
                this.dokumentDatum = undefined;
                this.kommentar = undefined;
                this.dokumentTyp = { code: "", id: "", displayName: "" };
            },

            changePatient(patient) {
                this.patientUUID = patient?.id;
                this.patient = patient;
            },

            async save() {
                // Fil-API:et för patientbundna filer är uppbyggt på ett speciellt sätt för att kunna ladda upp filer från webben,
                // därför skickar vi inte in en vanlig JSON-blob.
                const formData = new FormData();

                // Del av PatientSummaryHeader
                formData.append("patient", this.patientUUID);
                formData.append("documentTime", this.dokumentDatum);
                formData.append("documentTitle", this.kommentar);

                // Specifika för Filer
                formData.append("dokument_typ", this.dokumentTyp);

                const url = `/filhantering/${this.patientUUID}/fil/${this.fileId}/`;

                try {
                    await klinikenApi.put(url, formData, getConfig({}, "Filer"));
                    flashMessage("Fil uppdaterad");
                    this.$root.$emit("file-changed");
                    this.$emit("closetab");
                } catch (e) {
                    openDialog("Kunde inte spara filen: " + getErrorMessage(e));
                }
            },

            async connectToPatient() {
                const formData = {
                    patient: this.patientUUID,
                    documentTime: moment(this.dokumentDatum),
                    documentTitle: this.kommentar,
                    dokument_typ: this.dokumentTyp,
                };

                const url = `/filhantering/generellafiler/${this.fileId}/convert/`;

                try {
                    await klinikenApi.post(url, formData, getConfig({}, "Filer"));
                    flashMessage(
                        `Fil kopplad till patient – ${this.patient.personId_formatted} ${this.patient.fornamn} ${this.patient.efternamn}`
                    );

                    // Skickar tillbaka fildatan till Allmän filkö i den form listan behöver den
                    const fileData = {
                        id: this.fileId,
                        personId: this.patient.personId,
                        personId_formatted: this.patient.personId_formatted,
                        ...formData,
                    };
                    this.$root.$emit("file-connected", fileData);
                    this.$emit("closetab");
                } catch (e) {
                    openDialog("Kunde inte koppla filen: " + getErrorMessage(e));
                }
            },

            removeFile() {
                dialogEvents.$emit("open", {
                    type: "warning",
                    description: "Är du säker på att du vill radera filen?",
                    lines: [
                        "Det går inte att ångra.",
                        "",
                        'Kom ihåg att man inte får radera journalhandlingar. Kontrollera att filen inte är en journalhandling innan du klickar på "Ja, radera fil".',
                    ],
                    buttons: [
                        {
                            title: "Avbryt",
                            type: "secondary",
                        },
                        {
                            title: "Ja, radera fil",
                            type: "primary",
                            variant: "alert",
                            action: async () => {
                                try {
                                    await klinikenApi.delete(`filhantering/generellafiler/${this.fileId}`, getConfig());
                                    flashMessage("Fil raderad");
                                    this.$root.$emit("file-deleted", this.fileId);
                                    this.$emit("closetab");
                                } catch (e) {
                                    openDialog("Kunde inte radera filen: " + getErrorMessage(e));
                                }
                            },
                        },
                    ],
                });
            },

            async cancel() {
                if (!this.redigeringFormIsClean) {
                    dialogEvents.$emit("open", {
                        type: "warning",
                        description: "Du har inte sparat. Vill du stänga utan att spara?",
                        buttons: [
                            {
                                title: "Fortsätt redigera",
                                type: "secondary",
                            },
                            {
                                title: "Ja, stäng utan att spara",
                                type: "primary",
                                action: () => {
                                    this.$emit("closetab");
                                },
                            },
                        ],
                    });
                } else {
                    this.$emit("closetab");
                }
            },
        },
    };
</script>
<style lang="scss" scoped>
    @import "@/style/variables";

    .filhantering-redigera-fil-form {
        // TODO Ändra till form variable när branchen med PUR är inmergad
        max-width: 1100px;

        .margin-align {
            margin-left: 10px;
            margin-right: 10px;
        }

        h2 {
            margin: 0 0 20px 10px;
        }

        h3 {
            height: 28px;
            color: #277692;
            font-family: Roboto;
            font-size: 20px;
            letter-spacing: 0;
            line-height: 28px;
            display: inline-block;
            margin-left: 7px;
        }

        h4 {
            color: #277692;
            font-family: Roboto;
            font-size: 17px;
            letter-spacing: 0;
            line-height: 28px;
        }

        .fixed-width {
            max-width: 700px;
            margin-bottom: 29px;
        }

        .circle {
            height: 26px;
            width: 26px;
            background-color: #277692;
            border-radius: 13px;
        }

        .number {
            height: 26px;
            width: 100%;
            color: #ffffff;
            font-family: Roboto;
            font-size: 18px;
            letter-spacing: 0;
            line-height: 26px;
            text-align: center;
        }

        .vr {
            border-left: 1px solid #8ebaca;
            height: calc(100% - 60px);
            min-height: 10px;
            margin-left: 13px;
            margin-top: 10px;
            margin-bottom: 10px;

            &.vr-no-min-height {
                min-height: 0px;
            }
        }

        .k3-lock-border {
            height: 26px;
            width: 350px;
            background: linear-gradient(134.72deg, #c2e6e6 0%, rgba(194, 230, 228, 0) 100%);
            color: #277692;
            font-family: Roboto;
            font-size: 13px;
            font-weight: 500;
            letter-spacing: 0.46px;
            line-height: 26px;
            margin-bottom: 18px;
            margin-left: 10px;

            img {
                margin: -2px 6px 0px 17px;
            }
        }

        .k3-sub-form-flex-container {
            display: flex;
            flex-direction: row;
            margin-left: -20px;
        }

        .k3-sub-form {
            display: grid;
            width: 100%;
            grid-template-columns: 25% 25% 25% 25%;

            .whole {
                grid-column: 1 / span 4;
            }

            .input-grid-1-col {
                grid-column: span 1;
                min-width: 100%;
            }

            .input-grid-2-col {
                grid-column: span 2;
                min-width: 100%;
            }
        }

        .k3-dropdown-icon {
            margin-left: 15px;
        }

        .k3-img-rotate {
            transform: rotate(180deg);
        }

        .button-row {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
        }
    }
</style>
